import { DropzoneRef } from 'react-dropzone';
import { FlexBox } from '../../../common/wrappers';
import styled from 'styled-components';
import { ReactNode, useEffect, useState } from 'react';
import { Canvas } from 'fabric/fabric-impl';

import { generatePreviewImages } from '../apparel/utils';
import {
  PreviewBoundaryBox,
  PrintArea,
  VariantsV2,
} from '../../../../interface/catalog-interface-v2';
import ProductAngles from '../product-angles';
import { DataToGenerateAngleImage } from '../../../../interface';
import Loader from '../../../common/loader';
import { DropzonePlaceholder } from '../../../../assets/icons';
import {
  ProductName,
  ProductType,
} from '../../../../interface/product-interface';
import { getUtilityData } from '../../../../store/slices/utilitySlice';
import { useAppSelector } from '../../../../store/hooks';
import { colors } from '../../../../assets/colors';

interface Props {
  isTablet: boolean;
  addToGallery: (files: any) => void;
  files: any[];
  ToolBar: ReactNode;
  UndoRedoComponent: ReactNode;
  drawingBoard: Canvas | undefined;
  title: string;

  selectedAngle: {
    angle: string;
    image: string;
    originalImage: any;
  };
  dataToGenerateAngleImage: DataToGenerateAngleImage[];
  selectedVariant: VariantsV2 | undefined;
  printArea: PrintArea | undefined;
  handleAngleChange: (angle: string, source: string) => void;
  preloadedImages: {
    [key: string]: string;
  };
  addImageLoader: boolean;
  ornamentCanvasRef: React.RefObject<HTMLDivElement>;
  previewBoundaryBox: PreviewBoundaryBox;
  isAnglesLoading: boolean;
  dropzoneRef: React.MutableRefObject<DropzoneRef | null>;
  printAreaDimension: any;
}
const OrnamentEditor: React.FC<Props> = ({
  isTablet,
  addToGallery,
  files,
  ToolBar,
  UndoRedoComponent,
  drawingBoard,
  title,
  selectedVariant,
  dataToGenerateAngleImage,
  selectedAngle,
  printArea,
  handleAngleChange,
  preloadedImages,
  addImageLoader,
  ornamentCanvasRef,
  previewBoundaryBox,
  isAnglesLoading,
  dropzoneRef,
  printAreaDimension,
}) => {
  const [angleImages, setAngleImages] = useState<{ [key: string]: string }>();
  const { isSidebarExpanded } = useAppSelector(getUtilityData);

  useEffect(() => {
    async function startImageGeneration() {
      let copyAngles: any = { ...angleImages };
      for (const data of dataToGenerateAngleImage) {
        const { imageData, catalogData, variant, pixiApp, angle } = data;

        const img = await generatePreviewImages(
          imageData,
          catalogData,
          variant,
          pixiApp,
          angle,
          preloadedImages,
        );
        copyAngles = { ...copyAngles, [angle]: img };
      }
      setAngleImages(copyAngles);
    }
    startImageGeneration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataToGenerateAngleImage]);
  const canvasWidth = getCanvasWidthFromTitle(title);

  return (
    <>
      {(addImageLoader || isAnglesLoading) && <Loader disableBackDrop={true} />}
      {addImageLoader && <Loader disableBackDrop={true} />}
      <Wrapper isTablet={isTablet} isSidebarExpanded={isSidebarExpanded}>
        <div className="preview-wrapper">
          {selectedVariant && (
            <FlexBox>
              <ProductAngles
                isAnglesLoading={false}
                angle={selectedAngle.angle}
                selectedImage={selectedAngle.image}
                angleImages={angleImages}
                images={selectedVariant?.images}
                onClick={(angle, image) =>
                  handleAngleChange(angle, image.target.src)
                }
                printArea={printArea}
                currentProductType={ProductType.Ornament}
                previewBoundaryBox={previewBoundaryBox}
              />
            </FlexBox>
          )}
        </div>
        <DrawingWrapper>
          <FlexBox
            style={{
              width: '100%',
              height: 'auto',
            }}
            justifyContent="center"
            alignItems="center"
            direction="column"
            gap="2rem"
          >
            <FlexBox
              justifyContent="center"
              direction="column"
              alignItems="center"
              className="canvas"
              id="canvas-parent"
              style={{
                margin: '5%',
                position: 'relative',
              }}
              ref={ornamentCanvasRef}
              onClick={() => {
                if (dropzoneRef.current && files.length === 0) {
                  dropzoneRef.current.open();
                }
              }}
            >
              <>
                <section
                  style={{
                    display: files.length === 0 ? 'block' : 'none',
                    position: 'absolute',
                    zIndex: 10, // Ensure it overlays the canvas
                  }}
                >
                  <FlexBox
                    justifyContent="center"
                    alignItems="center"
                    direction="column"
                    style={{
                      width: '300px',
                    }}
                  >
                    <img src={DropzonePlaceholder} alt="Dropzone" />
                    <p className="fs-16" style={{ margin: '5px' }}>
                      Drag and drop your design here or{' '}
                      <span
                        style={{
                          color: colors.blue[900],
                        }}
                      >
                        Click to Upload
                      </span>
                    </p>
                    <FlexBox direction="column" className="text-align-center">
                      <span className="fs-14">
                        Print area size {printAreaDimension?.width} ×{' '}
                        {printAreaDimension?.height} px (300 DPI)
                      </span>

                      <span className="fs-14">
                        Maximum 25 MB (JPG, PNG, SVG)
                      </span>
                      <span className="fs-14">
                        Maximum resolution 25000 x 25000 px
                      </span>
                    </FlexBox>
                  </FlexBox>
                </section>
              </>

              <CanvasWrapper>
                <canvas height={500} width={canvasWidth} id="drawingArea" />
              </CanvasWrapper>
              {ToolBar}
            </FlexBox>
          </FlexBox>
        </DrawingWrapper>
        <div
          style={{
            position: 'absolute',
            bottom: '10px',
            left: '10px',
            userSelect: 'none',
          }}
        >
          {UndoRedoComponent}
        </div>
      </Wrapper>
    </>
  );
};
export default OrnamentEditor;
const Wrapper = styled.div<{ isTablet: boolean; isSidebarExpanded: boolean }>`
  padding: 16px 32px;
  display: flex;
  overflow: hidden;
  height: 85vh;
  // justify-content: center;
  gap: ${(props) => props.isTablet && '5rem'};
  .preview-wrapper {
    min-height: 35%;
    min-width: ${(props) => (props.isSidebarExpanded ? '30%' : '35%')};
  }
`;
const CanvasWrapper = styled.div`
  // position: absolute;
  top: 0;
  left: 0;
  // z-index: 150000;
`;
const DrawingWrapper = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  position: relative;

  .canvas {
    position: relative !important;
    background-color: #c2c2c2 !important;
  }
  .image-box {
    .mug-angle {
      margin-left: 128px;
    }
  }
`;

export const getCanvasWidthFromTitle = (title: string): number => {
  if (title.includes(ProductName.CircleOrnament)) {
    return 500;
  } else if (title.includes(ProductName.OvalOrnament)) {
    return 394;
  } else if (title.includes(ProductName.HeartOrnament)) {
    return 534;
  } else if (title.includes(ProductName.StarOrnament)) {
    return 515;
  } else if (title.includes(ProductName.TreeOrnament)) {
    return 432;
  } else if (title.includes(ProductName.BellOrnament)) {
    return 496;
  } else if (title.includes(ProductName.SnowflakeOrnament)) {
    return 465;
  } else if (title.includes(ProductName.BerlinOrnament)) {
    return 667;
  } else if (title.includes(ProductName.RectangleOrnament)) {
    return 328;
  } else if (title.includes(ProductName.TaperedOrnament)) {
    return 328;
  } else if (title.includes(ProductName.BeneluxOrnament)) {
    return 692;
  } else if (title.includes(ProductName.AluminiumOvalOrnament)) {
    return 404;
  } else if (title.includes(ProductName.CeramicStarOrnament)) {
    return 507;
  } else {
    return 500;
  }
};
